/**
 * Lazy-loaded Cookie Components
 * 
 * This module provides lazy-loaded versions of cookie-related components
 * to improve initial page load performance
 */
import React, { Suspense, lazy } from 'react';

import { useCookieConsent } from '../../hooks/useCookieConsent';

// Lazy load the cookie components
const CookieConsentBanner = lazy(() => import('./CookieConsentBanner'));
const CookieSettingsModal = lazy(() => import('./CookieSettingsModal'));

/**
 * Fallback component for cookie components
 */
const CookieFallback: React.FC = () => (
  <div className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg backdrop-blur-md p-4">
    <div className="flex items-center justify-center">
      <div className="animate-pulse text-sm text-gray-600 dark:text-gray-400">
        Loading cookie preferences...
      </div>
    </div>
  </div>
);

/**
 * Lazy Cookie Consent Banner Wrapper
 * Only loads the actual banner when it should be shown
 */
export const LazyCookieConsentBanner: React.FC = () => {
  const { showBanner } = useCookieConsent();

  // Don't render anything if banner shouldn't be shown
  if (!showBanner) {
    return null;
  }

  return (
    <Suspense fallback={<CookieFallback />}>
      <CookieConsentBanner />
    </Suspense>
  );
};

/**
 * Lazy Cookie Settings Modal Wrapper
 * Only loads the modal when it should be shown
 */
export const LazyCookieSettingsModal: React.FC = () => {
  const { showSettings } = useCookieConsent();

  // Don't render anything if modal shouldn't be shown
  if (!showSettings) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <CookieSettingsModal />
    </Suspense>
  );
};

/**
 * Combined Cookie Components
 * Provides both banner and modal with lazy loading
 */
export const CookieComponents: React.FC = () => {
  return (
    <>
      <LazyCookieConsentBanner />
      <LazyCookieSettingsModal />
    </>
  );
}; 